import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, CheckCircle2, ChevronDown, Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MedicareWaitlist from "@/components/MedicareWaitlist";
import { PRODUCT_ICON_MAP } from "@/components/ProductCard";
import { SERVICE_META } from "@/lib/serviceMeta";
import usePageSEO from "@/hooks/usePageSEO";

const TOOL_META = {
  "recommender": { label: "Product Recommender", note: "Match your situation to the right stack." },
  "life-insurance-needs": { label: "Life Insurance Needs", note: "Personalized coverage target." },
  "retirement-income": { label: "Retirement Income", note: "Model income with and without an annuity floor." },
  "college-savings": { label: "College Savings", note: "Project future cost, work back to a monthly target." },
  "medicare-eligibility": { label: "Medicare Eligibility", note: "Educational eligibility estimate." },
  "ltc-cost": { label: "LTC Cost Estimator", note: "State-specific care-cost projection." },
};

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx < 2);
  return (
    <div className="border-t border-lw-navy/10" data-testid={`service-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-5 text-left"
      >
        <span className="font-serif-display text-lg text-lw-navy leading-snug">{q}</span>
        <ChevronDown
          size={20}
          className={`text-lw-navy/60 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="pb-5 text-sm text-lw-navy/80 leading-relaxed" data-testid={`service-faq-body-${idx}`}>
          {a}
        </p>
      )}
    </div>
  );
}

export default function Service() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState("");

  const meta = SERVICE_META[slug] || {};
  const Icon = meta.Icon;

  const canonical = typeof window !== "undefined" ? window.location.href : undefined;
  usePageSEO({
    title: service ? `${service.name} — Lifespan Wealth Partners` : "Service",
    description: service?.meta_description,
    canonical,
    jsonLd: service && [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.name,
        description: service.meta_description,
        provider: {
          "@type": "FinancialService",
          name: "Lifespan Wealth Partners",
        },
        areaServed: "United States",
        serviceType: service.keyword_phrase,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (service.faqs || []).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiGet(`/services/${slug}`),
      apiGet("/products"),
      apiGet("/articles"),
    ])
      .then(([s, p, a]) => {
        if (cancelled) return;
        setService(s);
        setProducts(p || []);
        setArticles(a || []);
      })
      .catch(() => { if (!cancelled) setError("Service not found."); });
    return () => { cancelled = true; };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="pt-32 max-w-4xl mx-auto px-6"><p data-testid="service-not-found">{error}</p></main>
        <Footer />
      </div>
    );
  }
  if (!service) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="pt-32 max-w-4xl mx-auto px-6"><p>Loading…</p></main>
        <Footer />
      </div>
    );
  }

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const servicedProducts = (service.product_slugs || []).map((s) => productMap[s]).filter(Boolean);
  const relatedArticles = (service.related_article_slugs || [])
    .map((s) => articles.find((a) => a.slug === s))
    .filter(Boolean)
    .slice(0, 3);
  const applyHref = `/apply?source=service-${service.slug}`;

  return (
    <div data-testid={`service-page-${service.slug}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-24 sm:pt-28 lg:pt-32 pb-20">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <span className="lw-eyebrow" style={{ marginBottom: 0 }}>{service.eyebrow}</span>
                {service.medicare_gated && (
                  <span
                    data-testid="service-coming-soon-pill"
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-lw-navy text-lw-ivory text-[10px] font-semibold uppercase tracking-[0.14em]"
                  >
                    Coming Soon
                  </span>
                )}
              </div>
              <h1
                data-testid="service-headline"
                className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05]"
              >
                {service.hero_headline}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
                {service.hero_subhead}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={applyHref}
                  data-testid="service-cta-primary"
                  className="btn-gold"
                >
                  Book a no-obligation call <ArrowRight size={16} />
                </Link>
                {service.calculator_slugs?.length > 0 && (
                  <Link
                    to={`/tools/${service.calculator_slugs[0]}`}
                    data-testid="service-cta-secondary"
                    className="btn-outline-navy"
                  >
                    Explore our tools <ArrowRight size={16} />
                  </Link>
                )}
              </div>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <div
                className="aspect-[4/5] rounded-3xl p-10 flex items-end justify-start"
                style={{
                  background:
                    "linear-gradient(135deg, #0B2545 0%, #1E3A8A 60%, #2A4FBB 100%)",
                }}
              >
                {Icon && (
                  <Icon
                    size={140}
                    strokeWidth={0.9}
                    style={{ color: "#D4A24C" }}
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Medicare gated hero-banner */}
        {service.medicare_gated && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-10">
            <MedicareWaitlist context={{ source: `service-${service.slug}` }} />
          </section>
        )}

        {/* Concerns */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <span className="lw-eyebrow">What we help with</span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight max-w-3xl">
            The specific problems this service solves.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.concerns.map((c, i) => (
              <div
                key={i}
                data-testid={`service-concern-${i}`}
                className="lw-card p-6"
              >
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-lw-gold" />
                  <h3 className="font-serif-display text-lg text-lw-navy">{c.title}</h3>
                </div>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">{c.line}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Products under this service */}
        {servicedProducts.length > 0 && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
            <span className="lw-eyebrow">Products under {service.name}</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
              The specific solutions we place.
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {servicedProducts.map((p) => {
                const PIcon = PRODUCT_ICON_MAP[p.id];
                return (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    data-testid={`service-product-${p.id}`}
                    className="lw-card p-6 flex flex-col group"
                  >
                    <div className="flex items-start justify-between">
                      {PIcon && <PIcon size={20} className="text-lw-navy" />}
                      {p.is_available === false && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-lw-navy text-lw-ivory text-[10px] font-semibold uppercase tracking-[0.14em]">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <h3 className="mt-4 font-serif-display text-lg text-lw-navy">{p.name}</h3>
                    <p className="mt-2 text-sm text-lw-muted leading-relaxed">{p.tagline}</p>
                    <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-lw-navy font-semibold text-sm group-hover:gap-3 transition-all">
                      Learn more <ArrowRight size={14} />
                    </span>
                  </Link>
                );
              })}
            </div>
            {service.product_slug_placeholders?.length > 0 && (
              <p className="mt-6 text-xs text-lw-muted italic">
                Onboarding soon: {service.product_slug_placeholders.join(", ").replace(/-/g, " ")}.
              </p>
            )}
          </section>
        )}

        {/* Narrative — SEO body */}
        <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <span className="lw-eyebrow">How this fits your strategy</span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
            How {service.name.toLowerCase()} works, and where it fits.
          </h2>
          <p
            data-testid="service-narrative-intro"
            className="mt-6 text-base text-lw-navy/85 leading-relaxed"
          >
            {service.narrative_intro}
          </p>
          {service.narrative_sections.map((sec, i) => (
            <div key={i} className="mt-10" data-testid={`service-narrative-${i}`}>
              <h3 className="font-serif-display text-xl sm:text-2xl text-lw-navy leading-snug">{sec.heading}</h3>
              <div className="gold-hairline w-12 my-4 opacity-70" />
              <p className="text-base text-lw-navy/85 leading-relaxed">{sec.body}</p>
            </div>
          ))}
        </section>

        {/* Calculators */}
        {service.calculator_slugs?.length > 0 && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
            <span className="lw-eyebrow">Calculators for this service</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
              Model it before you buy it.
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.calculator_slugs.map((t) => {
                const tm = TOOL_META[t] || { label: t, note: "" };
                return (
                  <Link
                    key={t}
                    to={`/tools/${t}`}
                    data-testid={`service-tool-${t}`}
                    className="lw-card p-6 group"
                  >
                    <h3 className="font-serif-display text-lg text-lw-navy">{tm.label}</h3>
                    <p className="mt-2 text-sm text-lw-muted leading-relaxed">{tm.note}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-lw-navy font-semibold text-sm group-hover:gap-3 transition-all">
                      Try it <ArrowRight size={14} />
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Personas */}
        {service.persona_slugs?.length > 0 && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
            <span className="lw-eyebrow">Who this fits</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
              We serve clients across life stages.
            </h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {service.persona_slugs.map((p) => (
                <Link
                  key={p}
                  to={`/for/${p}`}
                  data-testid={`service-persona-${p}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-lw-navy/15 bg-white text-sm text-lw-navy hover:border-lw-gold transition-colors"
                >
                  {p.replace(/-/g, " ")} <ArrowRight size={14} />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQs — SEO-critical */}
        <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
          <span className="lw-eyebrow">Common questions</span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
            About {service.name.toLowerCase()}.
          </h2>
          <div className="mt-8 border-b border-lw-navy/10">
            {service.faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </section>

        {/* Compliance notes */}
        {service.compliance_notes?.length > 0 && (
          <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 mt-16">
            <div
              data-testid="service-compliance-notes"
              className="rounded-2xl border border-lw-navy/10 bg-white p-6 sm:p-7"
            >
              <span className="lw-eyebrow">Regulatory notes for this service</span>
              <div className="mt-4 space-y-4">
                {service.compliance_notes.map((n, i) => (
                  <div key={i} data-testid={`service-compliance-note-${i}`}>
                    <p className="text-xs uppercase tracking-[0.14em] font-semibold text-lw-navy/70">
                      {n.label}
                    </p>
                    <p className="mt-1 text-xs text-lw-muted leading-relaxed">{n.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20">
            <span className="lw-eyebrow">Related reading</span>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/learn/${a.slug}`}
                  data-testid={`service-article-${a.slug}`}
                  className="lw-card p-6 group"
                >
                  <span className="text-[10px] uppercase tracking-[0.14em] text-lw-gold font-semibold">
                    {a.category}
                  </span>
                  <h3 className="mt-2 font-serif-display text-lg text-lw-navy leading-snug">{a.title}</h3>
                  <p className="mt-2 text-sm text-lw-muted leading-relaxed">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-lw-navy font-semibold text-sm group-hover:gap-3 transition-all">
                    Read <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div
            className="rounded-3xl p-10 sm:p-14 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0B2545 0%, #142F58 60%, #1E3A8A 100%)",
            }}
          >
            <div className="relative z-10 max-w-2xl">
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-lw-gold">
                No pressure, no cost
              </span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-ivory leading-tight">
                {service.final_cta}
              </h2>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={applyHref} data-testid="service-final-cta" className="btn-gold">
                  Book a conversation <ArrowRight size={16} />
                </Link>
                <Link to="/strategy" className="btn-outline-light">
                  See the full strategy <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div
              className="absolute top-0 right-0 w-1/2 h-full opacity-20 hidden lg:block"
              style={{
                background:
                  "radial-gradient(circle at 70% 40%, rgba(212,162,76,0.5), transparent 60%)",
              }}
            />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
